import axios, {
  type AxiosRequestConfig,
  type AxiosResponse,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";
import { nanoid } from "nanoid";

export interface RequestConfig extends AxiosRequestConfig {
  /** 是否需要auth */
  isAuth?: boolean;
  /** 是否需要加密 */
  isEncryption?: boolean;
}

/**
 * 请求拦截器 - 添加请求ID和日志记录
 * @param instance axios实例
 */
const requestInterceptor = (instance: AxiosInstance) => {
  instance.interceptors.request.use(
    (requestConfig: InternalAxiosRequestConfig) => {
      const requestId = nanoid();
      requestConfig.params = { ...requestConfig.params, requestId };
      // console.log(`[Request ID: ${requestId}] Sending request to ${requestConfig.url}`);
      return requestConfig;
    },
    (error: any) => {
      console.error('Request interceptor error:', error);
      return Promise.reject(error);
    }
  );
};

/**
 * 响应拦截器 - 处理成功响应和错误响应
 * @param instance axios实例
 */
const responseInterceptor = (instance: AxiosInstance) => {
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      // const requestId = response.config.params?.requestId;
      // console.log(`[Request ID: ${requestId}] Response received - Status: ${response.status}`);
      return response;
    },
    (error: any) => {
      const requestId = error.config?.params?.requestId;
      const status = error.response?.status || 'Network Error';
      console.error(`[Request ID: ${requestId}] Request failed - Status: ${status}`);
      return Promise.reject(error);
    }
  );
};



/**
 * 创建基础 axios 实例的工厂函数
 * 只包含基本的请求ID追踪和日志记录功能
 */
export const createBaseAxiosInstance = (config: {
  baseURL?: string;
  timeout?: number;
  headers?: Record<string, string>;
  withCredentials?: boolean;
} = {}): AxiosInstance => {
  const instance = axios.create({
    headers: {
      "Content-Type": "application/json",
      ...config.headers,
    },
    baseURL: config.baseURL,
    timeout: config.timeout || 30000,
    withCredentials: config.withCredentials || false,
  });

  // 设置请求拦截器
  requestInterceptor(instance);

  // 设置响应拦截器
  responseInterceptor(instance);

  return instance;
};

/**
 * 默认的基础 axios 实例
 * 不设置 baseURL，由使用方自行配置
 */
export const axiosInstance = createBaseAxiosInstance({baseURL:'https://holopix.cn'});

/**
 * 基础请求函数
 * 只做最基本的请求处理，不做业务逻辑处理
 */
export const request = <T = any>(config: RequestConfig): Promise<T> => {
  return axiosInstance(config);
};
